import React, { useState } from 'react';
import { subscribe } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Check, ArrowRight } from 'lucide-react';
import { track } from '@/lib/track';

export default function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null); // null, 'sending', 'new', 'existing', 'resubscribed', 'error'
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('sending');
    try {
      const result = await subscribe(email.trim(), window.location.pathname);
      setStatus(result.status);
      setMessage(result.message);
      track('newsletter_subscribed', { page: window.location.pathname });
      if (result.status === 'new') setEmail('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setStatus('error');
      setMessage(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    }
  };

  const success = status === 'new' || status === 'resubscribed';
  const existing = status === 'existing';

  return (
    <section className="py-14 sm:py-20 bg-[#3A4035]" data-testid="newsletter-section">
      <div className="max-w-xl mx-auto px-4 sm:px-8 text-center">
        {success || existing ? (
          <div className="animate-fade-in" data-testid="newsletter-success">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
              <Check className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <p className="text-base text-white font-medium mb-2">{message}</p>
          </div>
        ) : (
          <>
            <h3 className="font-heading text-xl sm:text-2xl font-medium text-white tracking-tight mb-3">
              Good things are happening locally.
            </h3>
            <p className="text-sm text-white/70 leading-relaxed mb-2">
              New studios. Inspiring people. Fresh possibilities.
            </p>
            <p className="text-sm text-white/60 mb-6">
              Get the latest Go Freestyle stories, openings and opportunities, straight to your inbox.
            </p>

            {status === 'error' && (
              <p className="text-xs text-[#F0D4D4] mb-4" data-testid="newsletter-error">{message}</p>
            )}

            <form onSubmit={handleSubmit} className="flex gap-2 max-w-md mx-auto mb-4">
              <Input
                type="email" value={email} onChange={e => { setEmail(e.target.value); if (status === 'error') setStatus(null); }}
                placeholder="Your email address" required
                className="flex-1 bg-white/10 border-white/15 rounded-full h-11 text-white placeholder:text-white/40 text-sm focus:bg-white/15 focus:border-white/30 focus:ring-0"
                data-testid="newsletter-email"
              />
              <Button type="submit" disabled={status === 'sending' || !email.trim()}
                className="bg-white text-[#3A4035] rounded-full px-5 h-11 text-sm font-medium hover:bg-[#F2F0EB] shrink-0"
                data-testid="newsletter-submit">
                {status === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Keep me in the loop <ArrowRight className="w-3.5 h-3.5 ml-1.5" strokeWidth={1.5} /></>}
              </Button>
            </form>

            <p className="text-[10px] text-white/35 leading-relaxed">
              By subscribing, you agree to receive Go Freestyle news and updates by email. Unsubscribe anytime.{' '}
              <a href="/privacy" className="underline hover:text-white/50">Privacy Policy</a>
            </p>
          </>
        )}
      </div>
    </section>
  );
}
